'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { applyTraceHeaders } from '../functions/observability/traceHeaders';

type BaseArgs = {
  url: string;
  params?: Record<string, unknown>;
};

async function fetchPage<T>(args: BaseArgs, offset: number, limit: number): Promise<T[]> {
  const response = await axios({
    url: `/api/server/${args.url}`,
    method: 'post',
    headers: applyTraceHeaders({
      'Content-Type': 'application/json',
    }) as Record<string, string>,
    data: {
      url: args.url,
      method: 'get',
      params: { ...args.params, offset, limit },
    },
  });

  const data = response.data;
  if (
    data &&
    typeof data === 'object' &&
    'success' in data &&
    (data as { success?: boolean }).success === false
  ) {
    throw new Error(
      typeof (data as { error?: string }).error === 'string'
        ? (data as { error: string }).error
        : 'Server request failed',
    );
  }

  return Array.isArray(data) ? data : [];
}

/**
 * Paginated GET-via-proxy with offset/limit accumulation.
 * Refetches when the JSON-serialized request key changes (not when the caller passes a new object
 * with the same url/params — that avoids infinite fetch loops).
 */
export default function useServerInfiniteSearch<T>(
  baseArgs: BaseArgs | null,
  pageSize: number,
): {
  items: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  error: Error | undefined;
} {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Parent often passes a new `baseArgs` object each render; only the serialized key should
  // trigger fetches (see effect deps). Keep latest args on a ref for fetch/loadMore.
  const baseArgsRef = useRef<BaseArgs | null>(null);
  baseArgsRef.current = baseArgs;

  const serializedKey =
    baseArgs === null
      ? null
      : JSON.stringify({ url: baseArgs.url, params: baseArgs.params ?? {} });

  const loadMoreInFlight = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!serializedKey) {
      requestIdRef.current += 1;
      setItems([]);
      setHasMore(false);
      setError(undefined);
      setIsLoading(false);
      return;
    }

    const args = baseArgsRef.current;
    if (!args) return;

    const id = ++requestIdRef.current;
    setItems([]);
    setHasMore(false);
    setError(undefined);
    setIsLoading(true);

    fetchPage<T>(args, 0, pageSize)
      .then((page) => {
        if (requestIdRef.current !== id) return;
        setItems(page);
        setHasMore(page.length === pageSize);
      })
      .catch((e: unknown) => {
        if (requestIdRef.current !== id) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setItems([]);
        setHasMore(false);
      })
      .finally(() => {
        if (requestIdRef.current !== id) return;
        setIsLoading(false);
      });
  }, [serializedKey, pageSize]);

  const loadMore = useCallback(() => {
    const args = baseArgsRef.current;
    if (!args || !hasMore || isLoading || isLoadingMore || loadMoreInFlight.current) return;

    loadMoreInFlight.current = true;
    setIsLoadingMore(true);
    const offset = items.length;

    fetchPage<T>(args, offset, pageSize)
      .then((page) => {
        setItems((prev) => [...prev, ...page]);
        setHasMore(page.length === pageSize);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        setIsLoadingMore(false);
        loadMoreInFlight.current = false;
      });
  }, [hasMore, isLoading, isLoadingMore, items.length, pageSize]);

  return { items, isLoading, isLoadingMore, hasMore, loadMore, error };
}
